import { MoreHorizontal, Pencil, Pin, Search } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

function TitlebarIconButton({ label, active, onClick, children }) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      aria-label={label}
      aria-pressed={active}
      title={label}
      onClick={onClick}
      className={cn(
        'h-[26px] w-[26px] rounded-[7px] text-muted-foreground hover:bg-muted',
        active && 'bg-primary/10 text-primary hover:bg-primary/15 hover:text-primary',
      )}
    >
      {children}
    </Button>
  );
}

/**
 * The notes window's slim titlebar: drag region + pin (always-on-top), search toggle and
 * the ⋯ menu. Reuses the app titlebar chrome classes; traffic lights / window controls
 * come from the OS (hiddenInset / titleBarOverlay).
 */
function NotesTitlebar({ pinned, onTogglePin, searchOpen, onToggleSearch, onClearCompleted, onExport }) {
  const [platform, setPlatform] = useState('linux');
  const [menuOpen, setMenuOpen] = useState(false);
  const isDarwin = platform === 'darwin';

  useEffect(() => {
    const setupPlatform = async () => {
      const nextPlatform = await window.electronAPI?.getPlatform?.();
      if (nextPlatform) {
        setPlatform(nextPlatform);
      }
    };
    setupPlatform();
  }, []);

  return (
    <header className={cn('titlebar shrink-0 gap-2', isDarwin ? 'titlebar-darwin' : 'titlebar-notes-desktop')}>
      <div className="flex min-w-0 flex-1 items-center gap-1.5">
        <Pencil className="size-[13px] shrink-0 text-muted-foreground" aria-hidden />
        <span className="truncate text-xs font-bold text-muted-foreground">My Notes</span>
      </div>
      <div className="titlebar-actions no-drag gap-0.5">
        <TitlebarIconButton
          label={pinned ? 'Unpin (always on top)' : 'Pin — always on top'}
          active={pinned}
          onClick={onTogglePin}
        >
          <Pin className="size-[13px]" aria-hidden />
        </TitlebarIconButton>
        <TitlebarIconButton label="Search notes" active={searchOpen} onClick={onToggleSearch}>
          <Search className="size-3.5" aria-hidden />
        </TitlebarIconButton>
        <Popover open={menuOpen} onOpenChange={setMenuOpen}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label="More actions"
              className="h-[26px] w-[26px] rounded-[7px] text-muted-foreground hover:bg-muted"
            >
              <MoreHorizontal className="size-3.5" aria-hidden />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-1" align="end">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-8 w-full justify-start px-3 text-xs font-medium"
              onClick={() => {
                setMenuOpen(false);
                onClearCompleted?.();
              }}
            >
              Clear completed to-dos
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-8 w-full justify-start px-3 text-xs font-medium"
              onClick={() => {
                setMenuOpen(false);
                onExport?.();
              }}
            >
              Export as text
            </Button>
          </PopoverContent>
        </Popover>
      </div>
    </header>
  );
}

export default NotesTitlebar;
